import React, { useState, useEffect, useMemo } from 'react';

import { ProfilesResponse } from '@/features/user/types';
import { useAnalytics } from '@/lib/analytics';

import { SELECTION_OPTIONS } from '../../constants';
import type { FilteredCourseData, ProcessedBatch } from '../../types';
import ViewAllCard from '../join-waitlist-card/view-all-card';
import WaitlistCard from '../join-waitlist-card/waitlist-card';
import { SessionCard } from '../session-card';

import { EmptyList } from './empty-list';

interface TagDetail {
  color: string;
  name: string;
  text: string;
}

interface BatchesListProps {
  selectedCourse?: ProfilesResponse.EnrolledCourse;
  courseName: string;
  courseData: FilteredCourseData;
  index: number;
  selectedBatches: Record<string, string>;
  onBatchSelection: (courseName: string, batchId: string) => void;
  onWaitlistSelection: (courseName: string) => void;
  getTagDetails: (tags: string[]) => TagDetail[];
  selectedDays?: string[];
  batchAssignStatus: string;
  timezone?: string;
}

export const BatchesList: React.FC<BatchesListProps> = ({
  selectedCourse,
  courseName,
  courseData,
  index,
  selectedBatches,
  onBatchSelection,
  onWaitlistSelection,
  getTagDetails,
  selectedDays = [],
  batchAssignStatus,
  timezone,
}) => {
  const { trackEvent } = useAnalytics();

  const [showAllBatches, setShowAllBatches] = useState(false);

  useEffect(() => {
    if (courseData?.filtered_batches?.length === 0) {
      setShowAllBatches(true);
    }
  }, [courseData.filtered_batches]);

  const otherBatchesHeaderText = useMemo(() => {
    if (selectedDays.length > 0) {
      return `Filtered Batches (${selectedDays.join(', ')})`;
    }
    if (courseData?.filtered_batches?.length > 0) {
      return 'Other Batches';
    }
    return 'All Batches';
  }, [selectedDays, courseData?.filtered_batches?.length]);

  const handleBatchSelection = (courseName: string, batchId: string) => {
    trackEvent('BatchSelected', 1, {
      courseName,
      batchId,
    });
    onBatchSelection(courseName, batchId);
  };

  const handleWaitlistSelection = (courseName: string) => {
    trackEvent('WaitlistSelected', 1, {
      courseName,
    });
    onWaitlistSelection(courseName);
  };

  const renderSessionCard = (
    batch: ProcessedBatch,
    isRecommended: boolean = false
  ) => {
    return (
      <SessionCard
        key={String(batch.batch_id)}
        scheduleDetails={
          batch.scheduleDetails as Array<{
            day: string;
            time: string;
          }>
        }
        startDate={String(batch.startDate)}
        timezone={timezone}
        tagDetails={getTagDetails(batch.tags)}
        isSelected={selectedBatches[courseName] === String(batch.batch_id)}
        onClick={() => handleBatchSelection(courseName, String(batch.batch_id))}
        isRecommended={isRecommended}
        tentative={batch.tentative}
      />
    );
  };

  const renderMainContent = () => {
    if (batchAssignStatus === 'batch_already_assigned') {
      return (
        <h3 className="text-lg text-magical-moonlight-d1">
          This user was previously assigned to a batch, please contact admin.
        </h3>
      );
    }

    if (
      courseData.filtered_batches.length === 0 &&
      courseData.other_batches.length === 0
    ) {
      return (
        <EmptyList
          onJoinWaitlist={() => handleWaitlistSelection(courseName)}
          isSelected={
            selectedBatches[courseName] === SELECTION_OPTIONS.WAITLIST
          }
        />
      );
    }

    return (
      <>
        {/* Filtered Batches */}
        {courseData.filtered_batches.length > 0 && (
          <div className="flex flex-col gap-3">
            <h4 className="text-lg font-semibold text-magical-moonlight-d3 mb-4">
              Recommended Batches
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6 justify-items-center">
              {courseData.filtered_batches.map(batch =>
                renderSessionCard(batch, true)
              )}
              {showAllBatches ? null : (
                <ViewAllCard
                  onCtaClick={() => setShowAllBatches(!showAllBatches)}
                />
              )}
            </div>
          </div>
        )}

        {/* Other Batches */}
        {showAllBatches ? (
          <div className="flex flex-col gap-3">
            <h4 className="text-lg font-semibold text-magical-moonlight-d3 my-4">
              {otherBatchesHeaderText}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 justify-items-center">
              {courseData.other_batches.map(batch =>
                renderSessionCard(batch, false)
              )}
              {/* Waitlist Option */}
              <div className="flex flex-wrap gap-6 w-full">
                <WaitlistCard
                  courseName={courseName}
                  onCtaClick={() => handleWaitlistSelection(courseName)}
                  className={
                    selectedBatches[courseName] === SELECTION_OPTIONS.WAITLIST
                      ? 'ring-2 ring-hot-sun'
                      : ''
                  }
                  isWaitlistActive={
                    selectedCourse?.waitlist_status?.status === 'ACTIVE'
                  }
                />
              </div>
            </div>
          </div>
        ) : null}
      </>
    );
  };

  return (
    <div
      className={`flex flex-col gap-4 p-4 sm:px-4 md:px-10 ${
        index % 2 === 1 ? 'lg:bg-purplish-blue-light-cardbg' : ''
      } ${index < 1 ? 'pr-0' : ''}`}
    >
      <div className="w-full md:max-w-(--max-width) mx-auto">
        <h3 className="text-xl font-bold text-magical-moonlight-d4 capitalize mb-4">
          {`${courseName} Batches`}
        </h3>
        {renderMainContent()}
      </div>
    </div>
  );
};
